'use client';
import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useLocation } from '@/lib/hooks/useLocation';
import { FALLBACK_COORDS, haversineKm, getLocationState } from '@/lib/location';
import { NS_FACILITIES } from '@/lib/data/nsFacilities';

// ─── Types ────────────────────────────────────────────────────────────────────

type Volume = 'low' | 'moderate' | 'high';
type FacilityType = 'er' | 'walkin' | 'pharmacy';
type FilterType = 'all' | 'er' | 'walkin' | 'pharmacy';
type DayKey = 'sun' | 'mon' | 'tue' | 'wed' | 'thu' | 'fri' | 'sat';
type WeekHours = { [k in DayKey]?: [number, number] }; // [openHour24, closeHour24], omitted = closed

interface Facility {
  id: string;
  siteCode?: string;
  name: string;
  city: string;
  address: string;
  phone: string;
  lat: number;
  lng: number;
  type: FacilityType;
  wait?: string;
  waitMinutes?: number;
  status?: 'open' | 'busy' | 'closed';
  volume: Volume;
  badge?: string;
  isClosed?: boolean;
  hasLiveData?: boolean;
  hours?: WeekHours;
}

// ─── Phone numbers for CDN-tracked ER facilities ──────────────────────────────

const ER_PHONES: Record<string, string> = {
  QE2: '(902) 473-2220',
  DGH: '(902) 465-8300',
  HCH: '(902) 792-2000',
  CCH: '(902) 869-6100',
  CRH: '(902) 893-5554',
  VRH: '(902) 678-7381',
  SSR: '(902) 527-5000',
  YRH: '(902) 742-3541',
  CBR: '(902) 567-8000',
  ICH: '(902) 258-2100',
  SMR: '(902) 863-2830',
  ARH: '(902) 752-7600',
  CRC: '(902) 667-3361',
  QGH: '(902) 354-3400',
};

// ─── Static walk-in & pharmacy data (no live source available) ────────────────

const STATIC_FACILITIES: Facility[] = NS_FACILITIES as unknown as Facility[];

// ─── Helpers ──────────────────────────────────────────────────────────────────

const VOLUME_COLORS: Record<Volume, string> = {
  low:      '#7ebc59',
  moderate: '#f9a825',
  high:     '#d32f2f',
};

const STATUS_COLORS: Record<string, string> = {
  open:   '#7ebc59',
  busy:   '#f9a825',
  closed: '#9aa5b4',
};

const DAY_KEYS: DayKey[] = ['sun','mon','tue','wed','thu','fri','sat'];
const DAY_LABELS: Record<DayKey, string> = { sun:'Sun', mon:'Mon', tue:'Tue', wed:'Wed', thu:'Thu', fri:'Fri', sat:'Sat' };

function isOpenNow(h: WeekHours): boolean {
  const now = new Date();
  const dh = h[DAY_KEYS[now.getDay()]];
  if (!dh) return false;
  const cur = now.getHours() + now.getMinutes() / 60;
  return cur >= dh[0] && cur < dh[1];
}

function fmtHour(h: number): string {
  if (h === 0) return '12am';
  if (h === 12) return '12pm';
  return h < 12 ? `${h}am` : `${h - 12}pm`;
}

function formatHoursDisplay(h: WeekHours): string {
  const segs: string[] = [];
  let i = 0;
  while (i < 7) {
    const key = DAY_KEYS[i];
    const dh = h[key];
    const sig = JSON.stringify(dh ?? null);
    let j = i + 1;
    while (j < 7 && JSON.stringify(h[DAY_KEYS[j]] ?? null) === sig) j++;
    const range = i === j - 1 ? DAY_LABELS[key] : `${DAY_LABELS[key]}–${DAY_LABELS[DAY_KEYS[j - 1]]}`;
    segs.push(dh ? `${range} ${fmtHour(dh[0])}–${fmtHour(dh[1])}` : `${range} Closed`);
    i = j;
  }
  return segs.join(' · ');
}

function formatWait(minutes: number): string {
  if (minutes < 60) return `${minutes}m`;
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return m === 0 ? `${h}h` : `${h}h ${m}m`;
}

function driftMinutes(base: number): number {
  return Math.max(5, base + Math.floor((Math.random() - 0.5) * 20));
}

function computeVolume(minutes: number): Volume {
  if (minutes <= 60) return 'low';
  if (minutes <= 120) return 'moderate';
  return 'high';
}

// Convert CDN EDFacility records into our Facility shape
function cdnToFacility(ed: {
  siteCode: string; siteName: string; community: string; address: string;
  lat: number; lng: number; waitMinutes: number; highCapacity: boolean;
  isClosed: boolean; closureEnd?: string;
}, idx: number): Facility {
  const min = ed.isClosed ? 0 : ed.waitMinutes;
  const vol = computeVolume(min);
  return {
    id:           `er-cdn-${idx}`,
    siteCode:     ed.siteCode,
    name:         ed.siteName,
    city:         ed.community,
    address:      ed.address,
    phone:        ER_PHONES[ed.siteCode] ?? '',
    lat:          ed.lat,
    lng:          ed.lng,
    type:         'er',
    waitMinutes:  ed.isClosed ? undefined : min,
    wait:         ed.isClosed ? 'Closed' : formatWait(min),
    volume:       vol,
    badge:        ed.highCapacity ? 'HIGH CAPACITY' : undefined,
    isClosed:     ed.isClosed,
    hasLiveData:  true,
  };
}

// ─── Component ────────────────────────────────────────────────────────────────

export default function MapClient() {
  const mapRef          = useRef<HTMLDivElement>(null);
  const mapInstanceRef  = useRef<any>(null);
  const clusterGroupRef = useRef<any>(null);
  const markersRef      = useRef<any[]>([]);
  const userMarkerRef   = useRef<any>(null);
  const resizeObsRef    = useRef<ResizeObserver | null>(null);
  const resizeTimerRef  = useRef<ReturnType<typeof setTimeout> | null>(null);
  const handleResizeRef = useRef<(() => void) | null>(null);

  const { coords, status: locationStatus } = useLocation();

  const [mapReady,    setMapReady]    = useState(false);
  const [filter,      setFilter]      = useState<FilterType>('all');
  const [selectedId,  setSelectedId]  = useState<string | null>(null);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
  const [erError,     setErError]     = useState(false);

  // Start with walk-ins + pharmacies; ERs are populated from live API
  const [facilities, setFacilities] = useState<Facility[]>(() =>
    STATIC_FACILITIES.map((f) => {
      if (f.type !== 'walkin') return f;
      const min = f.waitMinutes ? driftMinutes(f.waitMinutes) : undefined;
      return { ...f, waitMinutes: min, wait: min ? formatWait(min) : undefined, volume: min ? computeVolume(min) : f.volume };
    })
  );

  // ── Fetch live ER data from our API route ──────────────────────────────────
  const loadERData = useCallback(async () => {
    try {
      const res = await fetch('/api/ns-ed-waittimes');
      if (!res.ok) throw new Error(`status ${res.status}`);
      const data = await res.json();
      const erFacilities: Facility[] = (data.facilities ?? []).map(cdnToFacility);
      setFacilities((prev) => [
        ...prev.filter((f) => f.type !== 'er' || f.hasLiveData === false),
        ...erFacilities,
      ]);
      setLastUpdated(new Date(data.fetchedAt));
      setErError(false);
    } catch {
      setErError(true);
    }
  }, []);

  useEffect(() => {
    loadERData();
    const id = setInterval(loadERData, 5 * 60 * 1000); // refresh every 5 min
    return () => clearInterval(id);
  }, [loadERData]);

  // ── Drift walk-in wait times every 60s (no live source exists for these) ──
  useEffect(() => {
    const id = setInterval(() => {
      setFacilities((prev) =>
        prev.map((f) => {
          if (f.type !== 'walkin') return f;
          const min = f.waitMinutes ? driftMinutes(f.waitMinutes) : undefined;
          return { ...f, waitMinutes: min, wait: min ? formatWait(min) : undefined, volume: min ? computeVolume(min) : f.volume };
        })
      );
    }, 60_000);
    return () => clearInterval(id);
  }, []);

  // ── Map init (once) ────────────────────────────────────────────────────────
  useEffect(() => {
    if (typeof window === 'undefined' || !mapRef.current || mapInstanceRef.current) return;

    Promise.all([import('leaflet'), import('leaflet.markercluster')]).then(([L]) => {
      if (!mapRef.current || mapInstanceRef.current) return;

      const el = mapRef.current as any;
      if (el._leaflet_id) el._leaflet_id = null;

      delete (L.Icon.Default.prototype as any)._getIconUrl;
      L.Icon.Default.mergeOptions({
        iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
        iconUrl:       'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
        shadowUrl:     'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
      });

      const map = L.map(mapRef.current!, {
        center: [45.1, -63.0],
        zoom: 7,
        zoomControl: true,
        dragging: true, scrollWheelZoom: true, touchZoom: true,
        doubleClickZoom: true, boxZoom: true, keyboard: true,
      });
      mapInstanceRef.current = map;

      L.tileLayer('https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png', {
        attribution: '© <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors © <a href="https://carto.com/attributions">CARTO</a>',
        subdomains: 'abcd',
        maxZoom: 19,
      }).addTo(map);

      const { coords: initialCoords } = getLocationState();
      const startPos = initialCoords ?? FALLBACK_COORDS;
      const userIcon = L.divIcon({
        html: `<div style="width:14px;height:14px;background:#204998;border:3px solid white;border-radius:50%;box-shadow:0 2px 8px rgba(32,73,152,0.5);"></div>`,
        className: '', iconSize: [14, 14], iconAnchor: [7, 7],
      });
      userMarkerRef.current = L.marker([startPos.lat, startPos.lng], { icon: userIcon })
        .addTo(map)
        .bindPopup(`<strong style="font-family:Inter,sans-serif;">Your Location</strong>${!initialCoords ? '<br/><span style="font-size:12px;color:#6b7a99;">Halifax, NS (approx.)</span>' : ''}`);
      if (initialCoords) map.setView([initialCoords.lat, initialCoords.lng], 10);

      requestAnimationFrame(() => { requestAnimationFrame(() => { map.invalidateSize(); }); });
      setTimeout(() => map.invalidateSize(), 150);

      const handleResize = () => {
        if (resizeTimerRef.current) clearTimeout(resizeTimerRef.current);
        resizeTimerRef.current = setTimeout(() => { map.invalidateSize(); }, 200);
      };
      handleResizeRef.current = handleResize;
      window.addEventListener('resize', handleResize);

      if (typeof ResizeObserver !== 'undefined' && mapRef.current) {
        resizeObsRef.current = new ResizeObserver(() => {
          if (resizeTimerRef.current) clearTimeout(resizeTimerRef.current);
          resizeTimerRef.current = setTimeout(() => { map.invalidateSize(); }, 150);
        });
        resizeObsRef.current.observe(mapRef.current);
      }

      setMapReady(true);
    });

    return () => {
      if (handleResizeRef.current) window.removeEventListener('resize', handleResizeRef.current);
      if (resizeTimerRef.current) clearTimeout(resizeTimerRef.current);
      if (resizeObsRef.current) { resizeObsRef.current.disconnect(); resizeObsRef.current = null; }
      if (mapInstanceRef.current) { mapInstanceRef.current.remove(); mapInstanceRef.current = null; }
    };
  }, []);

  // ── Move user marker when geolocation resolves ─────────────────────────────
  useEffect(() => {
    if (!coords || !mapInstanceRef.current || !userMarkerRef.current) return;
    userMarkerRef.current.setLatLng([coords.lat, coords.lng]);
    userMarkerRef.current.setPopupContent('<strong style="font-family:Inter,sans-serif;">Your Location</strong>');
    mapInstanceRef.current.setView([coords.lat, coords.lng], mapInstanceRef.current.getZoom());
  }, [coords]);

  // ── Rebuild markers whenever facilities, filter, or map readiness changes ──
  useEffect(() => {
    if (!mapReady || !mapInstanceRef.current) return;

    import('leaflet').then((L) => {
      if (!mapInstanceRef.current) return;

      if (clusterGroupRef.current) {
        clusterGroupRef.current.clearLayers();
        mapInstanceRef.current.removeLayer(clusterGroupRef.current);
        clusterGroupRef.current = null;
      }
      markersRef.current.forEach((m) => {
        try { mapInstanceRef.current.removeLayer(m); } catch { /* ignore */ }
      });
      markersRef.current = [];

      const visible = facilities.filter((f) => filter === 'all' || f.type === filter);

      const useCluster = visible.length >= 30 && (L as any).markerClusterGroup;
      let clusterGroup: any = null;
      if (useCluster) {
        clusterGroup = (L as any).markerClusterGroup({
          maxClusterRadius: 60,
          showCoverageOnHover: false,
          zoomToBoundsOnClick: true,
          iconCreateFunction: (cluster: any) => {
            const count = cluster.getChildCount();
            return L.divIcon({
              html: `
                <div style="position:relative;width:56px;height:56px;display:flex;align-items:center;justify-content:center;">
                  <!-- Pulsing outer ring -->
                  <div style="
                    position:absolute;inset:0;border-radius:50%;
                    border:2px solid rgba(32,73,152,0.35);
                    animation:atlasClusterPulse 2s ease-out infinite;
                  "></div>
                  <!-- Main circle -->
                  <div style="
                    width:44px;height:44px;
                    background:#204998;
                    border:3px solid white;
                    border-radius:50%;
                    box-shadow:0 2px 12px rgba(32,73,152,0.45);
                    display:flex;flex-direction:column;
                    align-items:center;justify-content:center;gap:1px;
                    position:relative;
                  ">
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="white">
                      <path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-2 10h-4v4h-2v-4H7v-2h4V7h2v4h4v2z"/>
                    </svg>
                    <span style="color:white;font-size:11px;font-weight:800;line-height:1;font-family:Inter,sans-serif;">${count}</span>
                  </div>
                </div>
                <style>
                  @keyframes atlasClusterPulse {
                    0%   { transform:scale(1);   opacity:.7; }
                    70%  { transform:scale(1.55); opacity:0; }
                    100% { transform:scale(1.55); opacity:0; }
                  }
                </style>`,
              className: '',
              iconSize: [56, 56],
              iconAnchor: [28, 28],
            });
          },
        });
        clusterGroupRef.current = clusterGroup;
      }

      visible.forEach((fac) => {
        const closedNow = !!(fac.hours && !isOpenNow(fac.hours));
        const effectiveStatus = closedNow ? 'closed' : (fac.status ?? 'open');
        const color = fac.type === 'pharmacy'
          ? STATUS_COLORS[effectiveStatus]
          : (fac.isClosed || fac.hasLiveData === false || closedNow)
            ? '#9aa5b4'
            : VOLUME_COLORS[fac.volume];

        const svgIcon =
          fac.type === 'er'
            ? `<svg width="14" height="14" viewBox="0 0 24 24" fill="white"><path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-2 10h-4v4h-2v-4H7v-2h4V7h2v4h4v2z"/></svg>`
            : fac.type === 'walkin'
            ? `<svg width="14" height="14" viewBox="0 0 24 24" fill="white"><path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/></svg>`
            : `<svg width="14" height="14" viewBox="0 0 24 24" fill="white"><path d="M6 3h12v2H6zm1 3h10l1 13H5L6 6zm4 2v2H8v2h2v6h2v-6h2V10h-2V8h-2z"/></svg>`;

        const icon = L.divIcon({
          html: `<div style="width:34px;height:34px;background:${color};border:3px solid white;border-radius:50%;box-shadow:0 2px 10px rgba(0,0,0,0.3);display:flex;align-items:center;justify-content:center;">${svgIcon}</div>`,
          className: '', iconSize: [34, 34], iconAnchor: [17, 17],
        });

        const hoursLine = fac.hours
          ? `<p style="color:#9aa5b4;font-size:10px;margin:3px 0 0;line-height:1.4;">🕐 ${formatHoursDisplay(fac.hours)}</p>`
          : '';

        const waitBlock =
          fac.type === 'pharmacy'
            ? `<div style="background:${closedNow ? '#f8faff' : '#f0f4fc'};border-radius:10px;padding:10px 14px;margin:10px 0;text-align:center;">
                 <p style="font-size:11px;color:#6b7a99;margin:0 0 2px;text-transform:uppercase;letter-spacing:.05em;">Status</p>
                 <p style="font-size:22px;font-weight:800;color:${color};font-family:'IBM Plex Mono',monospace;margin:0;">${effectiveStatus.charAt(0).toUpperCase() + effectiveStatus.slice(1)}</p>
               </div>`
            : fac.isClosed
            ? `<div style="background:#f8faff;border-radius:10px;padding:10px 14px;margin:10px 0;text-align:center;">
                 <p style="font-size:18px;font-weight:800;color:#9aa5b4;margin:0;">Temporarily Closed</p>
               </div>`
            : closedNow
            ? `<div style="background:#f8faff;border-radius:10px;padding:10px 14px;margin:10px 0;text-align:center;">
                 <p style="font-size:16px;font-weight:800;color:#9aa5b4;margin:0;">Currently Closed</p>
               </div>`
            : fac.hasLiveData === false
            ? `<div style="background:#f8faff;border-radius:10px;padding:10px 14px;margin:10px 0;text-align:center;">
                 <p style="font-size:11px;color:#9aa5b4;margin:0 0 2px;">${fac.badge === 'UTC' ? 'Urgent Treatment Centre — limited hours' : 'No live wait time data'}</p>
                 <p style="font-size:10px;color:#b0bac9;margin:0;">Call ahead to confirm availability</p>
               </div>`
            : `<div style="background:#f0f4fc;border-radius:10px;padding:10px 14px;margin:10px 0;text-align:center;">
                 <p style="font-size:11px;color:#6b7a99;margin:0 0 2px;text-transform:uppercase;letter-spacing:.05em;">Est. Wait · Live</p>
                 <p style="font-size:28px;font-weight:900;color:${color};font-family:'IBM Plex Mono',monospace;margin:0;line-height:1;">${fac.wait ?? '—'}</p>
                 ${fac.badge ? `<span style="display:inline-block;margin-top:4px;font-size:10px;font-weight:700;background:${color};color:white;padding:2px 8px;border-radius:4px;">${fac.badge}</span>` : ''}
               </div>`;

        const popup = `
          <div style="font-family:Inter,sans-serif;min-width:220px;padding:4px 0;">
            <p style="font-weight:800;color:#1a2744;margin:0 0 4px;font-size:15px;line-height:1.3;">${fac.name}</p>
            <p style="color:#6b7a99;font-size:12px;margin:0 0 1px;">📍 ${fac.address}, ${fac.city}, NS</p>
            <p style="color:#6b7a99;font-size:12px;margin:0;">📞 ${fac.phone}</p>
            ${hoursLine}
            ${waitBlock}
            <a href="https://www.google.com/maps/dir/?api=1&destination=${fac.lat},${fac.lng}"
               target="_blank" rel="noopener noreferrer"
               style="display:inline-block;padding:7px 16px;background:#204998;color:white;border-radius:999px;font-size:12px;font-weight:600;text-decoration:none;margin-top:2px;">
              Get Directions
            </a>
          </div>`;

        const marker = L.marker([fac.lat, fac.lng], { icon }).bindPopup(popup, { maxWidth: 260 });
        marker.on('click', () => setSelectedId(fac.id));
        markersRef.current.push(marker);

        if (useCluster && clusterGroup) clusterGroup.addLayer(marker);
        else marker.addTo(mapInstanceRef.current);
      });

      if (useCluster && clusterGroup) mapInstanceRef.current.addLayer(clusterGroup);
    });
  }, [facilities, filter, mapReady]);

  // ── Fly to facility on list click ──────────────────────────────────────────
  const flyToFacility = useCallback((fac: Facility) => {
    if (!mapInstanceRef.current) return;
    mapInstanceRef.current.flyTo([fac.lat, fac.lng], 14, { duration: 1 });
    setSelectedId(fac.id);
    const visible = facilities.filter((f) => filter === 'all' || f.type === filter);
    const idx = visible.findIndex((f) => f.id === fac.id);
    if (idx >= 0 && markersRef.current[idx]) markersRef.current[idx].openPopup();
  }, [facilities, filter]);

  // ── Derived lists ──────────────────────────────────────────────────────────
  const userLocation = coords ?? FALLBACK_COORDS;

  function distStr(fac: Facility): string {
    const km = haversineKm(userLocation.lat, userLocation.lng, fac.lat, fac.lng);
    return km < 1 ? `${Math.round(km * 1000)} m` : `${km.toFixed(1)} km`;
  }

  const erList       = [...facilities.filter((f) => f.type === 'er')].sort((a, b) => (a.waitMinutes ?? 9999) - (b.waitMinutes ?? 9999));
  const walkinList   = [...facilities.filter((f) => f.type === 'walkin')].sort((a, b) => (a.waitMinutes ?? 9999) - (b.waitMinutes ?? 9999));
  const pharmacyList = [...facilities.filter((f) => f.type === 'pharmacy')].sort((a, b) => {
    const o: Record<string, number> = { open: 0, busy: 1, closed: 2 };
    return (o[a.status ?? 'open'] ?? 1) - (o[b.status ?? 'open'] ?? 1);
  });

  const [isMobile, setIsMobile] = useState(false);
  const [sheetOpen, setSheetOpen] = useState(false);

  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 1024);
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);

  // ── Shared UI pieces ───────────────────────────────────────────────────────

  const facilityListContent = (
    <div style={{ flex: 1, overflowY: 'auto', padding: '8px 12px' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
        <p style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.1em', color: '#9aa5b4', margin: 0 }}>Wait Times</p>
        <span style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 10, fontWeight: 600, color: erError ? '#d32f2f' : '#7ebc59' }}>
          <span style={{ width: 6, height: 6, borderRadius: '50%', background: erError ? '#d32f2f' : '#7ebc59', display: 'inline-block' }} />
          {erError ? 'Error' : 'Live'}
        </span>
      </div>

      {(filter === 'all' || filter === 'er') && (
        <>
          <p style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.08em', color: '#b0bac9', margin: '4px 0 4px' }}>Emergency Depts</p>
          {erList.length === 0 && (
            <p style={{ fontSize: 11, color: '#9aa5b4', padding: '8px 0' }}>Loading live data…</p>
          )}
          {erList.map((fac) => {
            const color = (fac.isClosed || fac.hasLiveData === false) ? '#9aa5b4' : VOLUME_COLORS[fac.volume];
            const waitLabel = fac.isClosed ? 'Closed' : fac.hasLiveData === false ? 'No data' : (fac.wait ?? '—');
            return (
              <button key={fac.id} onClick={() => { flyToFacility(fac); if (isMobile) setSheetOpen(false); }}
                style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  width: '100%', textAlign: 'left', padding: '6px 6px 6px 0',
                  background: selectedId === fac.id ? '#f0f4fc' : 'transparent',
                  border: 'none', borderRadius: 6, cursor: 'pointer', gap: 6,
                }}>
                <div style={{ width: 3, alignSelf: 'stretch', borderRadius: 2, background: color, flexShrink: 0 }} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <p style={{ fontSize: 12, fontWeight: 600, color: '#1a2744', margin: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{fac.name}</p>
                  <p style={{ fontSize: 10, color: '#6b7a99', margin: 0 }}>{fac.city} · {distStr(fac)}</p>
                </div>
                <div style={{ textAlign: 'right', flexShrink: 0 }}>
                  <p style={{ fontSize: 13, fontWeight: 700, color, margin: 0, fontFamily: "'IBM Plex Mono', monospace" }}>{waitLabel}</p>
                  {fac.badge && <span style={{ fontSize: 9, fontWeight: 700, color, display: 'block' }}>{fac.badge}</span>}
                </div>
              </button>
            );
          })}
        </>
      )}

      {(filter === 'all' || filter === 'walkin') && (
        <>
          <p style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.08em', color: '#b0bac9', margin: '10px 0 4px' }}>Walk-In Clinics</p>
          {walkinList.map((fac) => {
            const color = VOLUME_COLORS[fac.volume];
            return (
              <button key={fac.id} onClick={() => { flyToFacility(fac); if (isMobile) setSheetOpen(false); }}
                style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  width: '100%', textAlign: 'left', padding: '6px 6px 6px 0',
                  background: selectedId === fac.id ? '#f0f4fc' : 'transparent',
                  border: 'none', borderRadius: 6, cursor: 'pointer', gap: 6,
                }}>
                <div style={{ width: 3, alignSelf: 'stretch', borderRadius: 2, background: color, flexShrink: 0 }} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <p style={{ fontSize: 12, fontWeight: 600, color: '#1a2744', margin: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{fac.name}</p>
                  <p style={{ fontSize: 10, color: '#6b7a99', margin: 0 }}>{fac.city} · {distStr(fac)}</p>
                </div>
                <p style={{ fontSize: 13, fontWeight: 700, color, margin: 0, flexShrink: 0, fontFamily: "'IBM Plex Mono', monospace" }}>~{fac.wait ?? '—'}</p>
              </button>
            );
          })}
        </>
      )}

      {(filter === 'all' || filter === 'pharmacy') && (
        <>
          <p style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.08em', color: '#b0bac9', margin: '10px 0 4px' }}>Pharmacies</p>
          {pharmacyList.map((fac) => {
            const color = STATUS_COLORS[fac.status ?? 'open'];
            const label = (fac.status ?? 'open').charAt(0).toUpperCase() + (fac.status ?? 'open').slice(1);
            return (
              <button key={fac.id} onClick={() => { flyToFacility(fac); if (isMobile) setSheetOpen(false); }}
                style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  width: '100%', textAlign: 'left', padding: '6px 6px 6px 0',
                  background: selectedId === fac.id ? '#f0f4fc' : 'transparent',
                  border: 'none', borderRadius: 6, cursor: 'pointer', gap: 6,
                }}>
                <div style={{ width: 3, alignSelf: 'stretch', borderRadius: 2, background: color, flexShrink: 0 }} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <p style={{ fontSize: 12, fontWeight: 600, color: '#1a2744', margin: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{fac.name}</p>
                  <p style={{ fontSize: 10, color: '#6b7a99', margin: 0 }}>{fac.city} · {distStr(fac)}</p>
                </div>
                <p style={{ fontSize: 12, fontWeight: 700, color, margin: 0, flexShrink: 0 }}>{label}</p>
              </button>
            );
          })}
        </>
      )}
    </div>
  );

  const filterBar = (
    <div style={{ display: 'flex', gap: 6, padding: '8px 12px', flexWrap: 'nowrap', overflowX: 'auto' }}>
      {(['all', 'er', 'walkin', 'pharmacy'] as FilterType[]).map((f) => {
        const labels: Record<FilterType, string> = { all: 'All', er: 'ERs', walkin: 'Walk-ins', pharmacy: 'Pharmacies' };
        return (
          <button key={f} onClick={() => setFilter(f)}
            style={{
              flexShrink: 0, padding: '5px 12px', borderRadius: 20,
              border: 'none', cursor: 'pointer', fontSize: 12, fontWeight: filter === f ? 700 : 500,
              background: filter === f ? '#204998' : '#f0f4fc',
              color: filter === f ? 'white' : '#4a5568',
              transition: 'background .15s',
            }}>
            {labels[f]}
          </button>
        );
      })}
    </div>
  );

  const syncFooter = (
    <div style={{ padding: '6px 12px', borderTop: '1px solid #f0f4fc', flexShrink: 0 }}>
      {lastUpdated ? (
        <p style={{ fontSize: 10, color: '#7ebc59', margin: '0 0 1px', fontWeight: 600 }}>
          ● ER data from NS Health · {lastUpdated.toLocaleTimeString('en-CA', { hour: '2-digit', minute: '2-digit' })}
        </p>
      ) : (
        <p style={{ fontSize: 10, color: '#9aa5b4', margin: '0 0 1px' }}>Loading ER wait times…</p>
      )}
      <p style={{ fontSize: 10, color: '#b0bac9', margin: 0 }}>Walk-in times are estimated · NS Health</p>
    </div>
  );

  // ─── Render ────────────────────────────────────────────────────────────────
  // mapRef div is always in the same DOM position so Leaflet never loses its container.
  return (
    <div style={{ flex: 1, display: 'flex', overflow: 'hidden' }}>

      {/* ── Desktop sidebar ────────────────────────────────────────────────── */}
      {!isMobile && (
        <div style={{ width: 335, flexShrink: 0, display: 'flex', flexDirection: 'column', background: 'white', borderRight: '1px solid #e4eaf5', overflow: 'hidden' }}>
          <div style={{ padding: '8px 12px', flexShrink: 0 }}>
            {(['all', 'er', 'walkin', 'pharmacy'] as FilterType[]).map((f) => {
              const labels: Record<FilterType, string> = { all: 'All', er: 'ERs', walkin: 'Walk-ins', pharmacy: 'Pharmacies' };
              return (
                <button key={f} onClick={() => setFilter(f)}
                  style={{
                    display: 'block', width: '100%', textAlign: 'left',
                    padding: '6px 10px', borderRadius: 8, marginBottom: 2,
                    border: 'none', cursor: 'pointer', fontSize: 13, fontWeight: filter === f ? 700 : 400,
                    background: filter === f ? '#204998' : 'transparent',
                    color: filter === f ? 'white' : '#4a5568',
                    transition: 'background .15s',
                  }}>
                  {labels[f]}
                </button>
              );
            })}
          </div>
          <div style={{ height: 1, background: '#f0f4fc', margin: '4px 0', flexShrink: 0 }} />
          {facilityListContent}
          {syncFooter}
        </div>
      )}

      {/* ── Map area — always rendered so mapRef is never re-mounted ──────── */}
      <div style={{ flex: 1, position: 'relative', overflow: 'hidden' }}>

        <div ref={mapRef} style={{ position: 'absolute', inset: 0 }} />

        {/* ── Map legend ── */}
        <div style={{
          position: 'absolute', top: 10, right: 10, zIndex: 1000,
          background: 'rgba(255,255,255,0.93)', backdropFilter: 'blur(4px)',
          border: '1px solid #e4eaf5', borderRadius: 10,
          padding: '8px 10px', boxShadow: '0 2px 8px rgba(0,0,0,0.10)',
          pointerEvents: 'none', display: 'flex', flexDirection: 'column', gap: 5,
          minWidth: 120,
        }}>
          {([
            { label: 'Emergency Dept', svg: `<svg width="11" height="11" viewBox="0 0 24 24" fill="white"><path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-2 10h-4v4h-2v-4H7v-2h4V7h2v4h4v2z"/></svg>` },
            { label: 'Walk-in Clinic', svg: `<svg width="11" height="11" viewBox="0 0 24 24" fill="white"><path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/></svg>` },
            { label: 'Pharmacy',       svg: `<svg width="11" height="11" viewBox="0 0 24 24" fill="white"><path d="M6 3h12v2H6zm1 3h10l1 13H5L6 6zm4 2v2H8v2h2v6h2v-6h2V10h-2V8h-2z"/></svg>` },
          ] as { label: string; svg: string }[]).map(({ label, svg }) => (
            <div key={label} style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
              <div style={{
                width: 22, height: 22, borderRadius: '50%', background: '#7ebc59',
                border: '2px solid white', boxShadow: '0 1px 4px rgba(0,0,0,0.18)',
                display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
              }} dangerouslySetInnerHTML={{ __html: svg }} />
              <span style={{ fontSize: 11, color: '#3a4a6b', fontWeight: 500, lineHeight: 1.2 }}>{label}</span>
            </div>
          ))}
          <div style={{ height: 1, background: '#e4eaf5', margin: '1px 0' }} />
          {([
            { color: '#7ebc59', label: 'Low wait' },
            { color: '#f9a825', label: 'Moderate' },
            { color: '#d32f2f', label: 'High wait' },
            { color: '#9aa5b4', label: 'No live data' },
          ] as { color: string; label: string }[]).map(({ color, label }) => (
            <div key={label} style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
              <div style={{ width: 10, height: 10, borderRadius: '50%', background: color, flexShrink: 0 }} />
              <span style={{ fontSize: 10, color: '#6b7a99', lineHeight: 1.2 }}>{label}</span>
            </div>
          ))}
        </div>

        {/* Desktop: location fallback notice */}
        {!isMobile && locationStatus === 'denied' && (
          <div style={{
            position: 'absolute', bottom: 32, left: '50%', transform: 'translateX(-50%)',
            zIndex: 1000, background: 'rgba(255,255,255,0.92)', padding: '5px 14px',
            borderRadius: 20, fontSize: 11, color: '#6b7a99', border: '1px solid #e4eaf5',
            pointerEvents: 'none', whiteSpace: 'nowrap', boxShadow: '0 1px 6px rgba(0,0,0,0.08)',
          }}>
            Showing results for Halifax. Enable location for accurate distances.
          </div>
        )}

        {/* Mobile: location fallback notice */}
        {isMobile && locationStatus === 'denied' && (
          <div style={{
            position: 'absolute', top: 12, left: '50%', transform: 'translateX(-50%)',
            zIndex: 800, background: 'rgba(255,255,255,0.92)', padding: '5px 14px',
            borderRadius: 20, fontSize: 11, color: '#6b7a99', border: '1px solid #e4eaf5',
            pointerEvents: 'none', whiteSpace: 'nowrap', boxShadow: '0 1px 6px rgba(0,0,0,0.08)',
          }}>
            Showing Halifax. Enable location for distances.
          </div>
        )}

        {/* Mobile: persistent bottom tab — always visible, anchored above safe area */}
        {isMobile && !sheetOpen && (
          <div style={{
            position: 'absolute', bottom: 0, left: 0, right: 0, zIndex: 900,
            background: 'white', borderTop: '1px solid #e4eaf5',
            borderRadius: '20px 20px 0 0',
            boxShadow: '0 -4px 20px rgba(0,0,0,0.12)',
            paddingBottom: 'env(safe-area-inset-bottom, 0px)',
          }}>
            <button
              onClick={() => setSheetOpen(true)}
              style={{
                width: '100%', border: 'none', background: 'transparent',
                cursor: 'pointer', padding: '14px 20px',
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <div style={{
                  width: 36, height: 36, borderRadius: '50%', background: '#204998',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                }}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="white">
                    <path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-2 10h-4v4h-2v-4H7v-2h4V7h2v4h4v2z"/>
                  </svg>
                </div>
                <div style={{ textAlign: 'left' }}>
                  <p style={{ fontSize: 14, fontWeight: 700, color: '#1a2744', margin: 0, lineHeight: 1.2 }}>Wait Times &amp; Clinics</p>
                  <p style={{ fontSize: 11, color: '#6b7a99', margin: 0 }}>Tap to view ER waits, walk-ins &amp; pharmacies</p>
                </div>
              </div>
              <span style={{ fontSize: 20, color: '#204998', fontWeight: 700, lineHeight: 1 }}>↑</span>
            </button>
          </div>
        )}

        {/* Mobile: bottom sheet */}
        {isMobile && sheetOpen && (
          <>
            <div onClick={() => setSheetOpen(false)}
              style={{ position: 'absolute', inset: 0, zIndex: 900, background: 'rgba(0,0,0,0.25)' }} />
            <div style={{
              position: 'absolute', bottom: 0, left: 0, right: 0, zIndex: 1000,
              background: 'white', borderRadius: '20px 20px 0 0',
              boxShadow: '0 -4px 24px rgba(0,0,0,0.18)',
              display: 'flex', flexDirection: 'column',
              maxHeight: '70vh', overflow: 'hidden',
              paddingBottom: 'env(safe-area-inset-bottom, 0px)',
            }}>
              {/* Handle + header — solid white, no bleed-through */}
              <div style={{ background: 'white', borderRadius: '20px 20px 0 0', flexShrink: 0 }}>
                <div onClick={() => setSheetOpen(false)} style={{ padding: '12px 16px 8px', cursor: 'pointer' }}>
                  <div style={{ width: 40, height: 4, background: '#d1d9e6', borderRadius: 2, margin: '0 auto 12px' }} />
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <p style={{ fontSize: 15, fontWeight: 700, color: '#1a2744', margin: 0 }}>Wait Times &amp; Clinics</p>
                    <span style={{ fontSize: 12, color: '#9aa5b4', background: '#f0f4fc', padding: '3px 10px', borderRadius: 99 }}>Close ↓</span>
                  </div>
                </div>
                {filterBar}
                <div style={{ height: 1, background: '#e4eaf5' }} />
              </div>
              {facilityListContent}
              {syncFooter}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
